import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';

const KharchoJeetoEntryForm = ({ onClose }) => {
  const authState = useSelector((state) => state.authReducer);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isError, setIsError] = useState(false);
  const [fetchData, { loading }] = useFetch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    billNumber: '',
    billDate: '',
    amount: '',
    product: '',
    shopName: '',
    place: '',
    pinCode: '',
    category: '',
    quizAnswer: '', // new field
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const config = {
      url: '/HharchoJeeto/entryform',
      method: 'post',
      data: formData,
      headers: { Authorization: authState.token },
    };

    try {
      const response = await fetchData(config);

      if (response && response.status) {
        setSubmitStatus('🎉 Thank you! Your entry has been submitted.');
        setIsError(false);
        setFormData({
          billNumber: '',
          billDate: '',
          amount: '',
          product: '',
          shopName: '',
          place: '',
          pinCode: '',
          category: '',
          quizAnswer: '',
        });
      } else {
        setSubmitStatus('⚠️ Sorry, there was an issue submitting your entry.');
        setIsError(true);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('🚨 Something went wrong. Please try again later.');
      setIsError(true);
    }
  };

  return (
    <section
      id="entry-form"
      className="relative bg-cover pt-4 px-4 sm:px-6 md:px-20 bg-yellow-black text-white border border-fuchsia-500 text-center"
    >
      <h2 className="text-xl lg:text-2xl font-bold mb-4 text-yellow-200">
        Participate in Daily Contest 🏆
      </h2>
      <p className="text-lg mb-8 max-w-2xl mx-auto text-white">
        Provide your bill details and choose a category to participate!
      </p>

      <form
        onSubmit={handleSubmit}
        className="max-w-2xl mx-auto bg-gray-500 p-6 rounded-lg shadow-md text-left space-y-4"
      >
        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="h-10 w-full p-2 border rounded bg-gray-100 text-black text-m lg:text-lg"
          required
        >
          <option value="">Select Bill Type</option>
          <optgroup label="🎟️ I Have Ticket" className='bg-red-200'>
            <option value="Train or Bus Ticket">🚆🚌 Train or Bus Ticket</option>
            <option value="Movie Ticket">🎬 Movie Ticket</option>
            <option value="Sports Event Ticket">⚽🏏 Sports Event Ticket</option>
            <option value="Concert or Event Entry">🎟️🎤 Concert or Event Entry</option>
          </optgroup>
          <optgroup label="🧾 I Have Food Bill">
            <option value="Street Eats">🍽️ Street Eats: Samosa, Vada Pav, Pani Puri</option>
            <option value="South Indian Delights">🍛 South Indian Delights: Idly, Dosa, Uttapam</option>
            <option value="Quick Bites">🥚 Quick Bites: Omelet</option>
            <option value="Drinks">☕🥤 Drinks: Tea, Coffee, Lassi, Milk</option>
            <option value="Meals">🍱 Meals: Lunch, Dinner – Buffet, Thali or Plate</option>
          </optgroup>
          <optgroup label="📲 I Have Recharge Bill" className='bg-red-200'>
            <option value="Mobile Bill">📱 Monthly Mobile Bill</option>
            <option value="Cable Bill">📺 Monthly Cable Bill</option>
            <option value="OTT Bill">🎬 Monthly OTT Bill</option>
          </optgroup>
          <optgroup label="🛍️ Others" className="bg-red-200">
            <option value="Fashion">👗💍 Dress & Fashion</option>
            <option value="Books & Stationary">📚✏️ Books & Stationary</option>
            <option value="Vegetables & Fruits">🥦🍎 Vegetables & Fruits</option>
            <option value="Dairy Products">🥛🧈 Dairy Products</option>
            <option value="Bakery Products">🍞🧁 Bakery Products</option>
            <option value="Farmer's Utilities">🚜 Farmer's Utilities</option>
            <option value="Vehicle Fuel">⛽ Vehicle Fuel</option>
            <option value="LPG">🔥 LPG</option>
          </optgroup>
        </select>

        <input
          name="billNumber"
          value={formData.billNumber}
          onChange={handleChange}
          placeholder="Bill Number"
          className="w-full p-2 border rounded text-black"
          required
        />
        <input
          name="billDate"
          type="date"
          value={
            formData.billDate ||
            new Date().toISOString().split("T")[0] // today's date in yyyy-mm-dd
          }
          readOnly
          className="w-full p-2 border border-fuchsia-500 rounded bg-gray-100 text-black cursor-not-allowed"
          required
        />
        <input
          name="amount"
          type="number"
          value={formData.amount}
          onChange={handleChange}
          placeholder="Amount"
          className="w-full p-2 border rounded text-black"
          required
        />
        <input
          name="product"
          value={formData.product}
          onChange={handleChange}
          placeholder="Product / Item Name"
          className="w-full p-2 border rounded text-black"
          required
        />
        <input
          name="shopName"
          value={formData.shopName}
          onChange={handleChange}
          placeholder="Shop Name"
          className="w-full p-2 border rounded text-black"
          required
        />
        <input
          name="place"
          value={formData.place}
          onChange={handleChange}
          placeholder="Place"
          className="w-full p-2 border rounded text-black"
          required
        />
        <input
          name="pinCode"
          value={formData.pinCode}
          onChange={handleChange}
          placeholder="PIN Code"
          className="w-full p-2 border rounded text-black"
          required
        />

        {/* Quiz Question Section */}
        <div className="bg-black border border-fuchsia-500 text-white p-4 rounded-lg shadow-md">
          <p className="font-semibold text-m lg:text-lg mb-2 text-yellow-200">
            Quick Question: Which is India’s national fruit? 🍎🍌🥭🍇
          </p>
          <div className="space-y-2 text-white">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="quizAnswer"
                value="Apple"
                checked={formData.quizAnswer === 'Apple'}
                onChange={handleChange}
                required
              />
              <span className='text-white'>Apple</span> 🍎
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="quizAnswer"
                value="Banana"
                checked={formData.quizAnswer === 'Banana'}
                onChange={handleChange}
              />
              <span className='text-white'>Banana</span> 🍌
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="quizAnswer"
                value="Mango"
                checked={formData.quizAnswer === 'Mango'}
                onChange={handleChange}
              />
              <span className='text-white'>Mango</span> 🥭
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="quizAnswer"
                value="Grapes"
                checked={formData.quizAnswer === 'Grapes'}
                onChange={handleChange}
              />
              <span className='text-white'>Grapes</span> 🍇
            </label>
          </div>
        </div>

        <div className='flex flex-row gap-2'>
          <button
            type="submit"
            className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition"
          >
            Submit Entry
          </button>
          <button
            onClick={onClose}
            className="mb-4 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded"
          >
            Close
          </button>
        </div>

        {submitStatus && (
          <p
            className={`mt-4 text-center font-semibold ${
              isError ? 'text-red-600' : 'text-green-600'
            }`}
          >
            {submitStatus}
          </p>
        )}
      </form>
    </section>
  );
};

export default KharchoJeetoEntryForm;
